import { ChangeEvent, cloneElement, FC, isValidElement, ReactElement, ReactNode } from 'react';
import { Button, Chip, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ro';
import { FilterProps } from './types';
import { Flex } from '../flex';
import { DateRangePicker } from '../date-range-picker';

interface ActivePill {
	key: string;
	label: string;
	onDelete: () => void;
}

export const FilterBox: FC<FilterProps> = ({
	filters,
	setFilters,
	selectOptions,
	showSelectFilter = true,
	children,
	datePlaceholder = 'Selectează o dată',
	selectLabel = 'Selectează o opțiune',
	showSecondSelectFilter = false,
	selectSecondLabel = 'Selectează o opțiune',
	selectSecondOptions,
	showDate = true,
	search
}) => {
	const handleSelectChange = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		key: 'selectValue' | 'secondSelectValue'
	): void => {
		setFilters({ ...filters, [key]: event.target.value });
	};

	// read the passed-in search field's own value/onChange so we can add a search icon,
	// a clear button and an "active filter" pill that reuse the page's own state.
	const searchEl = isValidElement(search) ? (search as ReactElement<any>) : null;
	const searchValue: string = searchEl?.props?.value ?? '';
	const clearSearch = (): void => searchEl?.props?.onChange?.({ target: { value: '' } });

	const renderSearch = (): ReactNode => {
		if (!searchEl) return search;
		return cloneElement(searchEl, {
			slotProps: {
				...(searchEl.props.slotProps || {}),
				input: {
					...((searchEl.props.slotProps || {}).input || {}),
					startAdornment: (
						<InputAdornment position="start">
							<Search fontSize="small" />
						</InputAdornment>
					),
					...(searchValue
						? {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton size="small" aria-label="Șterge căutarea" onClick={clearSearch}>
											<Close fontSize="small" />
										</IconButton>
									</InputAdornment>
								)
							}
						: {})
				}
			}
		});
	};

	// end "X" button that clears a single select field (hides the dropdown arrow while shown)
	const clearAdornment = (key: 'selectValue' | 'secondSelectValue'): JSX.Element => (
		<InputAdornment position="end" sx={{ mr: 2 }}>
			<IconButton
				size="small"
				aria-label="Șterge filtrul"
				onMouseDown={(e) => e.stopPropagation()}
				onClick={(e) => {
					e.stopPropagation();
					setFilters({ ...filters, [key]: '' });
				}}
			>
				<Close fontSize="small" />
			</IconButton>
		</InputAdornment>
	);

	// ==============================|| ACTIVE FILTER PILLS ||============================== //
	const pills: ActivePill[] = [];
	if (searchValue) {
		pills.push({ key: 'q', label: `„${searchValue}”`, onDelete: clearSearch });
	}
	if (filters.selectValue) {
		const label = selectOptions?.find((o) => o.value === filters.selectValue)?.label ?? String(filters.selectValue);
		pills.push({
			key: 'sel',
			label: `${selectLabel}: ${label}`,
			onDelete: () => setFilters({ ...filters, selectValue: '' })
		});
	}
	if (filters.secondSelectValue) {
		const label =
			selectSecondOptions?.find((o) => o.value === filters.secondSelectValue)?.label ?? String(filters.secondSelectValue);
		pills.push({
			key: 'sel2',
			label: `${selectSecondLabel}: ${label}`,
			onDelete: () => setFilters({ ...filters, secondSelectValue: '' })
		});
	}
	if (filters.startDate || filters.endDate) {
		const fmt = (d?: string): string => (d ? dayjs(d).format('DD.MM.YYYY') : '…');
		pills.push({
			key: 'date',
			label: `Perioadă: ${fmt(filters.startDate)} - ${fmt(filters.endDate)}`,
			onDelete: () => setFilters({ ...filters, startDate: '', endDate: '' })
		});
	}

	const clearAll = (): void => {
		setFilters({ ...filters, selectValue: '', secondSelectValue: '', startDate: '', endDate: '' });
		clearSearch();
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
			<Stack sx={{ width: '100%', gap: 2 }}>
				{/* filter fields */}
				<Flex sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-start' }}>
					{renderSearch()}
					{showDate && (
						<DateRangePicker
							inputLabel={datePlaceholder}
							startDate={filters?.startDate}
							setFilters={setFilters}
							endDate={filters?.endDate}
						/>
					)}
					{showSelectFilter && selectOptions && (
						<TextField
							select
							label={selectLabel}
							value={filters.selectValue || ''}
							onChange={(e) => handleSelectChange(e, 'selectValue')}
							sx={{ width: '240px', ...(filters.selectValue && { '& .MuiSelect-icon': { display: 'none' } }) }}
							slotProps={{
								select: {
									MenuProps: {
										slotProps: {
											paper: {
												sx: {
													maxHeight: 350,
													width: '240px'
												}
											}
										}
									}
								},
								input: {
									endAdornment: filters.selectValue ? clearAdornment('selectValue') : undefined
								}
							}}
						>
							{selectOptions.map((option) => (
								<MenuItem
									key={option.value}
									value={option.value}
									sx={{
										whiteSpace: 'normal',
										wordBreak: 'break-word',
										display: 'block'
									}}
								>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					)}
					{showSecondSelectFilter && selectSecondOptions && (
						<TextField
							select
							label={selectSecondLabel}
							value={filters.secondSelectValue || ''}
							onChange={(e) => handleSelectChange(e, 'secondSelectValue')}
							sx={{ width: '240px', ...(filters.secondSelectValue && { '& .MuiSelect-icon': { display: 'none' } }) }}
							slotProps={{
								input: {
									endAdornment: filters.secondSelectValue ? clearAdornment('secondSelectValue') : undefined
								}
							}}
						>
							{selectSecondOptions.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					)}
					{children}
				</Flex>

				{/* active filters */}
				{pills.length > 0 && (
					<Flex sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
						<Typography variant="body2" sx={{ color: 'text.secondary' }}>
							Filtre active:
						</Typography>
						{pills.map((p) => (
							<Chip key={p.key} label={p.label} variant="outlined" onDelete={p.onDelete} />
						))}
						<Button color="error" size="small" onClick={clearAll}>
							Șterge tot
						</Button>
					</Flex>
				)}
			</Stack>
		</LocalizationProvider>
	);
};

FilterBox.defaultProps = {
	children: undefined
};
